import { ReactNode } from 'react';
import styled from 'styled-components';
import {
  ThemeType, useIsMsalAuth, useStyledTheme, WarningIcon,
} from '../../..';
import { BorderlessInputFocus } from '../form/inputs/Input';
import { Flex, FlexColumn } from '../Flex';

const TabsPerRow = 5;

// eslint-disable-next-line no-shadow
export enum TabType {
  PRIMARY,
  SECONDARY,
  DISABLED
}

export type Tab = {
  id: string,
  tabType: TabType,
  label: ReactNode,
  percentComplete?: number,
  hasRejectPolicyErrors?: boolean,
  hasRejectDataErrors?: boolean,
  hasReferWarnings?: boolean,
  disabled?: boolean,
  onClick: () => void,
}

const tabTypeToBackground = (tabType: TabType, theme: ThemeType) => ({
  [TabType.PRIMARY]: theme.colours.primary[1],
  [TabType.SECONDARY]: theme.colours.primary[9],
  [TabType.DISABLED]: theme.colours.neutral[7],
}[tabType]);

const tabTypeToColour = (tabType: TabType, theme: ThemeType) => ({
  [TabType.PRIMARY]: theme.colours.neutral[10],
  [TabType.SECONDARY]: theme.colours.primary[1],
  [TabType.DISABLED]: theme.colours.neutral[5],
}[tabType]);

const tabTypeToPercentageBackground = (tabType: TabType, theme: ThemeType) => ({
  [TabType.PRIMARY]: theme.colours.secondary[1],
  [TabType.SECONDARY]: theme.colours.primary[1],
  [TabType.DISABLED]: theme.colours.neutral[5],
}[tabType]);

export const Container = styled(FlexColumn)` ${({ theme }) => `
  gap:${theme.padding[2]};
  width:100%;
`}`;

export const TabNavContainer = styled(Flex)` ${({ theme }) => `
  gap:${theme.padding[2]};
`}`;

export const TabNavButton = styled.button<{tabType: TabType}>` ${({ theme, tabType }) => `
    width: calc(100% / ${TabsPerRow}); 
    display:flex;
    flex-direction: column;
    border: none;
    font-family: 'Open Sans', sans-serif;
    border-radius: ${theme.borderRadius[2]};
    background: ${tabTypeToBackground(tabType, theme)};
    color: ${tabTypeToColour(tabType, theme)};
    height: ${theme.headerHeight};
    align-items: center;
    justify-content: space-between;
    gap: ${theme.padding[2]};
    padding: ${theme.padding[5]} ${theme.padding[6]} ${theme.padding[2]} ${theme.padding[6]};
    box-sizing: border-box;
    cursor: default;

    font-size:${theme.fontSize[2]};
    font-weight: ${tabType === TabType.PRIMARY ? theme.fontWeight.bolder : theme.fontWeight.bold};
    line-height: ${theme.fontSize[6]};
    
    ${tabType !== TabType.DISABLED
    && ` 
      &:hover {
        box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.10);
      };
      cursor: pointer;
    `};
    ${tabType === TabType.PRIMARY && `
      box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.10);
    `};
  `}
  ${BorderlessInputFocus}
`;

export const TabNavLabel = styled.div` ${({ theme }) => `
  height: ${theme.padding[6]};
  display: inline-flex;
  font-size: ${theme.fontSize[2]};
  gap: calc(${theme.padding[2]} - ${theme.borderWidth[2]});
  align-items: center;
`}`;

export const TabPercentageCompleteContainer = styled(Flex)` ${({ theme }) => `
  border-radius:${theme.borderRadius[1]};
  background: ${theme.colours.neutral[10]};
  width: 100%;
  height: ${theme.borderWidth[3]};
`}`;

export const TabPercentageComplete = styled.div<{
  percentComplete: number,
  tabType: TabType,
}>` ${({
  theme, percentComplete, tabType,
}) => `
  height: 4px;
  border-radius:${theme.borderRadius[1]};
  background: ${tabTypeToPercentageBackground(tabType, theme)};
  width: ${percentComplete}%;
`}`;

export const TabLabelContainer = styled(Flex)` ${({ theme }) => `
  align-items: center;
  gap: ${theme.padding[2]};
`}`;

export const IconContainer = styled.div` ${({ theme }) => `
  width: ${theme.fontSize[6]};
  height: ${theme.fontSize[6]};
`}`;

export const EmptyButton = styled.div` ${() => `
  width: calc(100% / ${TabsPerRow});
  height: 100%;
`}`;

export function TabButton({ tab }: {tab: Tab}) {
  const theme = useStyledTheme();

  return (
    <TabNavButton key={tab?.id} onClick={tab?.onClick} tabType={tab?.disabled ? TabType.DISABLED : tab?.tabType} disabled={tab?.disabled ?? false}>
      <TabLabelContainer>
        { (tab.hasRejectPolicyErrors) && (
        <IconContainer>
          <WarningIcon stroke={theme.colours.warning[3]} fill={theme.colours.neutral[10]} />
        </IconContainer>
        )}
        { (tab.hasRejectDataErrors && !tab.hasRejectPolicyErrors) && (
        <IconContainer>
          <WarningIcon stroke={theme.colours.danger[1]} fill={theme.colours.neutral[10]} />
        </IconContainer>
        )}
        { (tab.hasReferWarnings && !tab.hasRejectPolicyErrors && !tab.hasRejectDataErrors) && (
        <IconContainer>
          <WarningIcon stroke={theme.colours.secondary[1]} fill={theme.colours.neutral[10]} />
        </IconContainer>
        )}
        <TabNavLabel>
          {tab.label}
        </TabNavLabel>
      </TabLabelContainer>
      {(tab.percentComplete !== undefined) && (
      <TabPercentageCompleteContainer>
        <TabPercentageComplete percentComplete={tab.percentComplete} tabType={tab.tabType} />
      </TabPercentageCompleteContainer>
      )}
    </TabNavButton>
  );
}

type TabNavProps = {
  tabs: Tab[],
  underwriterTabs: Tab[],
  submitButton: ReactNode,
}

export function TabNav({ tabs, underwriterTabs, submitButton }: TabNavProps) {
  const isUnderwriter = useIsMsalAuth();

  return (
    <Container>
      <TabNavContainer>
        {tabs.map((tab) => (
          <TabButton tab={tab} />
        ))}
        { Array.from(Array(TabsPerRow - 1 - tabs.length)).map(() => (<EmptyButton />)) }
        {!isUnderwriter ? submitButton : <EmptyButton />}
      </TabNavContainer>

      {isUnderwriter && (
        <TabNavContainer>
          {underwriterTabs.map((tab) => (
            <TabButton tab={tab} />
          ))}
          { Array.from(Array(TabsPerRow - 1 - underwriterTabs.length)).map(() => (<EmptyButton />)) }
          {submitButton}
        </TabNavContainer>
      )}
    </Container>
  );
}
